/**
 * One client's email, as staff run it — three sections of the client's
 * Services tab, all read from the same answer:
 *
 * - **the wallet**: its balance and movements, the price of an email (the
 *   platform's or its own) and the low-balance alert;
 * - **sending**: the client's own stop, and each of its domains with its own;
 * - **the do-not-email list**: addresses this client's email skips.
 *
 * The API decides everything that can be refused — a price under the minimum,
 * a top-up that is too small, an address that is not one — and its words are
 * shown where the value was typed. The screen checks none of it first.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  ReadOnly,
  Select,
  ServerTable,
  StatusChip,
  Switch,
  Textarea,
  api,
  createServerFetcher,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { formatMetered } from './ai-card';
import { ServiceSpend } from './service-spend';
import { refusalText } from './api-refusal';
import {
  EMAIL_ENDPOINT,
  emailKeys,
  type EmailClient,
  type EmailDomain,
  type EmailSuppression,
  type EmailSuppressionAdded,
  type EmailWalletEntry,
  type PauseBody,
} from './email';
import { PauseButton } from './email-pause';

interface Props {
  clientPublicId: string;
  clientName: string;
}

const clientUrl = (id: string) => `${EMAIL_ENDPOINT}/clients/${id}`;

function useEmailClient(clientPublicId: string) {
  return useQuery({
    queryKey: emailKeys.client(clientPublicId),
    queryFn: () => api.get<EmailClient>(clientUrl(clientPublicId)),
  });
}

/** The loading and failure of the shared read, said the same in each section. */
function Unavailable({ query }: { query: ReturnType<typeof useEmailClient> }) {
  if (query.isLoading) return <LoadingState label="Loading email" />;
  return (
    <ErrorState
      message={query.error instanceof ApiError ? query.error.message : 'Email could not be loaded.'}
      onRetry={() => void query.refetch()}
    />
  );
}

// ── the wallet ──────────────────────────────────────────────
const KIND_LABELS = {
  top_up: 'Top-up',
  refund: 'Refund',
  adjustment: 'Adjustment',
  hold: 'Email sent',
  release: 'Returned',
  inbound: 'Incoming email',
};
const KIND_TONES: Record<string, Tone> = {
  top_up: 'success',
  refund: 'success',
  adjustment: 'info',
  hold: 'neutral',
  release: 'neutral',
  inbound: 'neutral',
};

export function EmailWalletCard({ clientPublicId, clientName }: Props) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const url = clientUrl(clientPublicId);
  const key = emailKeys.client(clientPublicId);
  const email = useEmailClient(clientPublicId);

  const update = useMutation({
    mutationFn: (body: { price?: string | null }) =>
      api.patch<EmailClient>(url, body),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success('Saved');
    },
  });

  if (!email.data) return <Unavailable query={email} />;
  const data = email.data;
  const wallet = data.wallet;

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Mail className="size-4" />
            Email
          </span>
        }
        description={`${clientName}'s email is paid from its wallet. Each email is charged when it is sent, at the price below, before GST.`}
      />
      <CardBody className="space-y-3">
        {data.has_service ? null : (
          <Alert tone="info">
            {clientName} has no email service yet. Its wallet can be credited (Wallet &amp; credits) and its price set ahead of adding one.
          </Alert>
        )}
        <ServiceSpend
          balance={wallet.balance}
          currency={wallet.currency}
          low={wallet.low}
          spent={wallet.spent_30_days}
        />

        <PriceEditor
          // Keyed on what is saved, so a save shows the saved price again.
          key={`${data.own_price}|${data.platform_price}`}
          data={data}
          canEdit={canEdit}
          saving={update.isPending}
          refused={refusalText(update.error)}
          onChange={() => update.reset()}
          onSave={(price) => update.mutate({ price })}
        />

        <p className="text-xs font-medium text-muted">Email's movements</p>
        {wallet.entries.length === 0 ? (
          <p className="text-sm text-muted">No movements yet.</p>
        ) : (
          // Keyed on the newest movement: a new entry moves every page down by
          // one, so the earlier pages already loaded no longer line up.
          <Movements
            key={wallet.entries[0]?.public_id}
            url={url}
            first={wallet.entries}
            hasMore={wallet.has_more}
          />
        )}
      </CardBody>
    </Card>
  );
}

/** The movements, newest first, with the earlier ones a press away. */
function Movements({ url, first, hasMore }: { url: string; first: EmailWalletEntry[]; hasMore: boolean }) {
  const [earlier, setEarlier] = useState<EmailWalletEntry[]>([]);
  const [more, setMore] = useState(hasMore);
  const entries = [...first, ...earlier];
  const load = useMutation({
    // The API pages by time: everything before the oldest movement on screen.
    mutationFn: () => api.get<EmailClient>(url, { params: { before: entries[entries.length - 1]?.at } }),
    onSuccess: (data) => {
      setEarlier((was) => [...was, ...data.wallet.entries]);
      setMore(data.wallet.has_more);
    },
    onError: (error) => toast.error('Could not load them', refusalText(error)),
  });
  return (
    <>
      <table className="w-full text-xs">
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.public_id} className="border-t border-line">
              <td className="py-1.5 text-muted">{formatDateTime(entry.at)}</td>
              <td className="py-1.5">
                <StatusChip value={entry.kind} labels={KIND_LABELS} tones={KIND_TONES} />
              </td>
              <td className="max-w-xs truncate py-1.5 text-muted">
                {entry.description}
                {entry.usage.emails !== undefined && entry.usage.rate !== undefined
                  ? ` · ${formatNumber(entry.usage.emails)} × ${formatMetered(entry.usage.rate)}`
                  : ''}
              </td>
              <td className="py-1.5 text-right font-mono tabular">{formatMetered(entry.amount)}</td>
              <td className="py-1.5 pl-3 text-right font-mono tabular text-muted">{formatMetered(entry.balance_after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {more ? (
        <div className="flex justify-center">
          <Button size="sm" variant="ghost" loading={load.isPending} onClick={() => load.mutate()}>
            Earlier movements
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** What an email costs this client: the platform's price, or one agreed with it. */
function PriceEditor({
  data,
  canEdit,
  saving,
  refused,
  onChange,
  onSave,
}: {
  data: EmailClient;
  canEdit: boolean;
  saving: boolean;
  /** The server's answer to the last save. */
  refused: string | undefined;
  onChange: () => void;
  onSave: (price: string | null) => void;
}) {
  const [own, setOwn] = useState(data.own_price !== null);
  const [price, setPrice] = useState(data.own_price ?? data.platform_price);
  const changed = own !== (data.own_price !== null) || (own && price !== data.own_price);
  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-2 rounded border border-line p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-muted">Price per email (₹, before GST)</p>
          <Select
            size="sm"
            containerClassName="w-64"
            aria-label="Price"
            value={own ? 'own' : 'platform'}
            options={[
              { value: 'platform', label: "The platform's price" },
              { value: 'own', label: 'Its own price' },
            ]}
            onChange={(event) => {
              onChange();
              setOwn(event.target.value === 'own');
            }}
          />
        </div>
        {own ? (
          <Input
            size="sm"
            label="Its own price"
            inputMode="decimal"
            containerClassName="max-w-xs"
            value={price}
            hint={`The platform charges ${formatMetered(data.platform_price)}; nobody is charged under ${formatMetered(data.minimum_price)}.`}
            error={refused}
            onChange={(event) => {
              onChange();
              setPrice(event.target.value.trim());
            }}
          />
        ) : (
          <p className="text-xs text-muted">
            {formatMetered(data.platform_price)} an email — set under Email → Pricing.
            {refused ? (
              <span role="alert" className="block text-danger">
                {refused}
              </span>
            ) : null}
          </p>
        )}
        {/* What is charged can differ from what was agreed: a minimum raised
            since lifts an older, lower price without anybody editing it. */}
        {data.own_price !== null && data.wallet.price !== data.own_price ? (
          <p className="text-xs text-warning">
            Charged {formatMetered(data.wallet.price)} an email: its own price is under the minimum now.
          </p>
        ) : null}
        {canEdit && changed ? (
          <div className="flex justify-end">
            <Button size="sm" loading={saving} onClick={() => onSave(own ? price : null)}>
              Save the price
            </Button>
          </div>
        ) : null}
      </div>
    </ReadOnly>
  );
}

// ── sending: the client's stop, and its domains ─────────────
const DOMAIN_STATUS: Record<string, { label: string; tone: Tone }> = {
  verified: { label: 'Verified', tone: 'success' },
  pending: { label: 'Waiting for DNS', tone: 'warning' },
  failed: { label: 'Not verified', tone: 'danger' },
};

const STAGES: Record<string, string> = {
  new: 'New',
  building: 'Building',
  established: 'Established',
  custom: 'Custom caps',
};
const THROTTLES: Record<string, string> = {
  rates: 'its bounce or complaint rate is near the stop line',
  spike: 'it sent far more than usual today, for a day',
  service: 'the mail service reported an impact on reputation',
};

function StageDialog({
  domain,
  onClose,
  onSaved,
}: {
  domain: EmailDomain;
  onClose: () => void;
  onSaved: (data: EmailClient) => void;
}) {
  const [stage, setStage] = useState(domain.warmup_stage);
  const [daily, setDaily] = useState(String(domain.daily_cap));
  const [hourly, setHourly] = useState(String(domain.hourly_cap));
  const save = useMutation({
    mutationFn: () =>
      api.put<EmailClient>(`${EMAIL_ENDPOINT}/domains/${domain.public_id}/limits`, {
        stage,
        ...(stage === 'custom' ? { daily_cap: Number(daily), hourly_cap: Number(hourly) } : {}),
      }),
    onSuccess: (data) => {
      toast.success('Limits saved', `${domain.domain} is held to them from its next send.`);
      onSaved(data);
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Limits for ${domain.domain}`}
      description="A domain climbs its warm-up by itself while its record is good. Move it by hand after a review, or give it caps of its own."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          label="Stage"
          value={stage}
          options={Object.entries(STAGES).map(([value, label]) => ({ value, label }))}
          onChange={(event) => {
            save.reset();
            setStage(event.target.value);
          }}
        />
        {stage === 'custom' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="A day" inputMode="numeric" value={daily} onChange={(event) => setDaily(event.target.value)} />
            <Input label="An hour" inputMode="numeric" value={hourly} onChange={(event) => setHourly(event.target.value)} />
          </div>
        ) : null}
        {save.error ? <Alert tone="danger">{refusalText(save.error)}</Alert> : null}
      </div>
    </Modal>
  );
}

export function EmailSendingCard({ clientPublicId, clientName }: Props) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const [staging, setStaging] = useState<EmailDomain | null>(null);
  const key = emailKeys.client(clientPublicId);
  const email = useEmailClient(clientPublicId);

  const pauseClient = useMutation({
    mutationFn: (body: PauseBody) => api.put<EmailClient>(`${clientUrl(clientPublicId)}/sending`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success(
        data.sending_paused ? 'Email paused' : 'Email resumed',
        data.sending_paused ? `Nothing ${clientName} sends by email leaves.` : `${clientName}'s email is sent again.`,
      );
    },
  });
  const pauseDomain = useMutation({
    mutationFn: ({ domain, body }: { domain: EmailDomain; body: PauseBody }) =>
      api.put<EmailClient>(`${EMAIL_ENDPOINT}/domains/${domain.public_id}/sending`, body),
    onSuccess: (data, { domain, body }) => {
      queryClient.setQueryData(key, data);
      toast.success(body.paused ? `${domain.domain} paused` : `${domain.domain} resumed`);
    },
  });

  const tracking = useMutation({
    mutationFn: (on: boolean) => api.patch<EmailClient>(clientUrl(clientPublicId), { tracking: on }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success(data.tracking ? 'Opens and clicks are recorded' : 'Opens and clicks are no longer recorded');
    },
    onError: (error) => toast.error('Not changed', refusalText(error)),
  });

  if (!email.data) return <Unavailable query={email} />;
  const data = email.data;

  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-4">
        {staging ? (
          <StageDialog
            domain={staging}
            onClose={() => setStaging(null)}
            onSaved={(next) => {
              queryClient.setQueryData(key, next);
              setStaging(null);
            }}
          />
        ) : null}
        <Card>
          <CardHeader
            title="Email sending"
            description={`Whether ${clientName}'s email leaves at all. Paused, the client keeps every screen and is told only that sending is paused for review — never why.`}
            actions={
              <Badge tone={data.sending_paused ? 'danger' : 'success'}>{data.sending_paused ? 'Paused' : 'Sending'}</Badge>
            }
          />
          <CardBody className="space-y-3">
            {data.sending_paused ? (
              <Alert tone="danger">
                Paused {data.sending_paused_at ? `since ${formatDateTime(data.sending_paused_at)}` : ''}.
                {data.sending_paused_note ? ` Note: ${data.sending_paused_note}` : ' No note was left.'}
              </Alert>
            ) : (
              <p className="text-sm text-muted">
                {clientName}'s email is sent, unless all email is paused under Email → Sending or a domain below is.
              </p>
            )}
            <Switch
              label="Record opens and clicks"
              hint={
                data.tracking_available
                  ? `Of ${clientName}'s marketing email only — never a receipt or a code. Links are rewritten and a pixel is added, so turn it on only where the client has asked for it.`
                  : 'This installation has no tracking set configured, so opens and clicks cannot be recorded.'
              }
              checked={data.tracking} disabled={(!data.tracking_available && !data.tracking) || tracking.isPending}
              onChange={(event) => tracking.mutate(event.target.checked)}
            />
            {canEdit ? (
              <PauseButton
                paused={data.sending_paused}
                subject={`${clientName}'s email`}
                size="md"
                consequence={`Every email ${clientName} sends, schedules or has queued is refused until somebody resumes it. A refused send costs nothing.`}
                resumed={`${clientName}'s email is sent again. A domain paused on its own stays paused.`}
                onSet={(body) => pauseClient.mutateAsync(body)}
              />
            ) : null}
          </CardBody>
        </Card>
  
        <Card>
          <CardHeader
            title="Domains"
            description="What the client sends from. A domain can be paused on its own, leaving the client's others sending."
          />
          <CardBody>
            {data.domains.length === 0 ? (
              <p className="text-sm text-muted">{clientName} has not added a domain yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {data.domains.map((domain) => {
                  const status = DOMAIN_STATUS[domain.status] ?? { label: domain.status, tone: 'neutral' as Tone };
                  return (
                    <li key={domain.public_id} className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="truncate text-sm font-medium text-heading">{domain.domain}</p>
                          <Badge tone={status.tone}>{status.label}</Badge>
                          {domain.paused ? <Badge tone="danger">Paused</Badge> : null}
                        </div>
                        <p className="text-xs text-muted">
                          Project {domain.project} · {formatNumber(domain.accounts)} account{domain.accounts === 1 ? '' : 's'}
                          {domain.verified_at ? ` · verified ${formatDateTime(domain.verified_at)}` : ''}
                        </p>
                        <p className="text-xs text-muted">
                          {STAGES[domain.warmup_stage] ?? domain.warmup_stage} · {formatNumber(domain.sent_last_day)} of{' '}
                          {formatNumber(domain.daily_cap)} in 24 hours · {formatNumber(domain.sent_last_hour)} of{' '}
                          {formatNumber(domain.hourly_cap)} this hour
                        </p>
                        <p className="text-xs text-muted">
                          Last 7 days: {formatNumber(domain.week.accepted)} sent · bounces{' '}
                          {domain.week.bounce_rate === null ? '—' : `${domain.week.bounce_rate}%`} · complaints{' '}
                          {domain.week.complaint_rate === null ? '—' : `${domain.week.complaint_rate}%`}
                          {data.tracking ? ` · opened ${domain.week.open_rate === null ? '—' : `${domain.week.open_rate}%`}` : ''}
                        </p>
                        {domain.links ? (
                          <p className="text-xs text-muted">
                            Link address <span className="font-mono">{domain.links.host}</span> ·{' '}
                            {LINK_STATES[domain.links.state] ?? domain.links.state}
                          </p>
                        ) : null}
                        {domain.throttled ? (
                          <p className="text-xs text-warning">
                            Held to half its caps — {THROTTLES[domain.throttle_reason ?? ''] ?? 'by Connect\'s watch'}.
                          </p>
                        ) : null}
                        {domain.paused ? (
                          <p className="text-xs text-danger">
                            Paused {domain.paused_at ? `since ${formatDateTime(domain.paused_at)}` : ''}
                            {domain.paused_note ? ` — ${domain.paused_note}` : ' — no note was left'}
                          </p>
                        ) : null}
                        {/* For whoever has to find the project at the mail
                            service; nobody else needs to read it. */}
                        <p className="font-mono text-2xs text-faint">Reference {domain.project_reference}</p>
                      </div>
                      {canEdit ? (
                        <div className="flex flex-wrap items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setStaging(domain)}>
                          Limits
                        </Button>
                        <PauseButton
                          paused={domain.paused}
                          subject={domain.domain}
                          consequence={`Email from addresses at ${domain.domain} is refused until somebody resumes it. ${clientName}'s other domains keep sending.`}
                          resumed={`Email from ${domain.domain} is sent again, unless ${clientName}'s email or all email is paused.`}
                          onSet={(body) => pauseDomain.mutateAsync({ domain, body })}
                        />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </ReadOnly>
  );
}

/** Where a domain's unsubscribe links point, as staff read it. */
const LINK_STATES: Record<string, string> = {
  pending: 'not set up — links use Connect’s own address',
  connecting: 'waiting for a certificate — links use Connect’s own address',
  ready: 'in use',
};

// ── the do-not-email list ───────────────────────────────────
const REASON_LABELS: Record<string, string> = {
  manual: 'Blocked',
  stop: 'Opted out',
  bounce: 'Bounced',
  complaint: 'Marked as spam',
  unsubscribe: 'Unsubscribed',
};
const REASON_TONES: Record<string, Tone> = {
  manual: 'neutral',
  stop: 'warning',
  bounce: 'danger',
  complaint: 'danger',
  unsubscribe: 'info',
};
const ADDED_BY_LABELS: Record<string, string> = {
  client: 'The client',
  staff: 'Staff',
  delivery: 'Delivery report',
  recipient: 'The recipient',
};

export function EmailSuppressionCard({ clientPublicId, clientName }: Props) {
  const queryClient = useQueryClient();
  const canAdd = useCan('messaging.email.create');
  const canLift = useCan('messaging.email.edit');
  const [emails, setEmails] = useState('');
  const [reason, setReason] = useState<'manual' | 'stop'>('manual');
  const [lifting, setLifting] = useState<EmailSuppression | null>(null);
  const url = `${clientUrl(clientPublicId)}/suppressions`;
  const listKey = useMemo(() => emailKeys.suppressions(clientPublicId), [clientPublicId]);
  const fetchRows = useMemo(() => createServerFetcher<EmailSuppression>(url), [url]);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey });

  const add = useMutation({
    mutationFn: () =>
      api.post<EmailSuppressionAdded>(url, { emails: emails.split(/[\s,;]+/).filter(Boolean), reason }),
    onSuccess: (result) => {
      setEmails('');
      refresh();
      toast.success(
        `${formatNumber(result.added)} added`,
        [
          result.already_suppressed ? `${formatNumber(result.already_suppressed)} were already on it` : null,
          result.invalid.length ? `not email addresses: ${result.invalid.join(', ')}` : null,
        ]
          .filter(Boolean)
          .join('; ') || undefined,
      );
    },
  });

  const lift = useMutation({
    mutationFn: (row: EmailSuppression) => api.delete<null>(`${url}/${row.public_id}`),
    onSuccess: () => {
      refresh();
      toast.success('Lifted', `${clientName}'s email goes to that address again.`);
    },
    onError: (error) => toast.error('Not lifted', refusalText(error)),
  });

  const columns = useMemo<ServerColumn<EmailSuppression>[]>(
    () => [
      { field: 'email', header: 'Address', width: 260 },
      {
        field: 'reason',
        header: 'Why',
        width: 150,
        render: (row) => <Badge tone={REASON_TONES[row.reason] ?? 'neutral'}>{REASON_LABELS[row.reason] ?? row.reason}</Badge>,
      },
      { field: 'added_by', header: 'Added by', width: 150, render: (row) => ADDED_BY_LABELS[row.added_by] ?? row.added_by },
      { field: 'created_at', header: 'Added', width: 170, render: (row) => formatDateTime(row.created_at) },
      { field: 'note', header: 'Note', sortable: false, render: (row) => <span className="text-muted">{row.note}</span> },
      // The column is there only for somebody who may lift.
      ...(canLift
        ? [
            {
              field: 'public_id',
              header: '',
              width: 60,
              sortable: false,
              render: (row: EmailSuppression) => (
                <Button variant="ghost" size="sm" aria-label={`Lift ${row.email}`} onClick={() => setLifting(row)}>
                  <Trash2 className="size-3.5" />
                </Button>
              ),
            } satisfies ServerColumn<EmailSuppression>,
          ]
        : []),
    ],
    [canLift],
  );

  return (
    <Card>
      <CardHeader
        title="Do-not-email list"
        description={`Addresses ${clientName}'s email skips: blocked by hand, opted out, unsubscribed with the link in a message, bounced, or marked as spam.`}
      />
      <CardBody className="space-y-4">
        {canAdd ? (
          <>
            <div className="grid gap-3 md:grid-cols-[1fr_12rem_auto] md:items-end">
              <Textarea
                label="Add addresses"
                rows={2}
                placeholder="asha@example.com, ravi@example.org"
                hint="Separated by commas, spaces or new lines. Up to 5,000."
                value={emails}
                onChange={(event) => {
                  add.reset();
                  setEmails(event.target.value);
                }}
              />
              <Select
                label="Why"
                value={reason}
                options={[
                  { value: 'manual', label: 'Blocked' },
                  { value: 'stop', label: 'Opted out' },
                ]}
                onChange={(event) => setReason(event.target.value as 'manual' | 'stop')}
              />
              <Button className="md:mb-5" loading={add.isPending} disabled={!emails.trim()} onClick={() => add.mutate()}>
                Add
              </Button>
            </div>
            {add.error ? (
              <p role="alert" className="text-xs text-danger">
                {refusalText(add.error) ?? 'The addresses could not be added.'}
              </p>
            ) : null}
          </>
        ) : null}

        <ServerTable<EmailSuppression>
          columns={columns}
          fetchRows={fetchRows}
          rowKey={(row) => row.public_id}
          queryKey={listKey}
          stateKey="messaging.email.suppressions"
          searchPlaceholder="Search by address"
          fillHeight={false}
          emptyTitle="Nobody is on the list"
          emptyDescription="Addresses added here or by the client, and those learned from bounces, spam complaints and unsubscribes, appear here."
        />
      </CardBody>

      <Confirm
        open={lifting !== null}
        onClose={() => setLifting(null)}
        title={`Lift ${lifting?.email ?? ''}?`}
        confirmLabel="Lift"
        message={
          lifting && lifting.reason !== 'manual' && lifting.reason !== 'stop'
            ? `${clientName}'s email goes to that address again. It is on the list because it ${
                lifting.reason === 'bounce'
                  ? 'bounced'
                  : lifting.reason === 'complaint'
                    ? 'was marked as spam'
                    : 'unsubscribed'
              } — lift it only where that was a mistake.${
                lifting.reason === 'bounce'
                  ? ' An address that bounced is also under Email → Bounced addresses, and stays skipped until it is removed there.'
                  : ''
              }`
            : `${clientName}'s email goes to that address again.`
        }
        onConfirm={async () => {
          // The mutation reports its own failure; the dialog only closes.
          if (lifting) await lift.mutateAsync(lifting).catch(() => undefined);
        }}
      />
    </Card>
  );
}
